/**
 * v8.05 — Keep-Alive GLOBAL das telas operacionais (Cozinha, Entregador).
 *
 * Problema real (visto em produção): tablets Android suspendem os timers da
 * main thread quando a aba fica em segundo plano / tela apaga → heartbeats,
 * flush da fila e pollings congelam → "SINCRONIZAÇÃO PARADA" até alguém tocar.
 *
 * Blindagem em 3 frentes:
 *  1. Screen Wake Lock: mantém a tela LIGADA enquanto a página operacional
 *     está visível (tablets de parede nunca dormem). Re-adquirido ao voltar.
 *  2. Web Worker heartbeat (25s): timers em Worker NÃO sofrem o throttling
 *     agressivo da main thread → cada tick chama kickSync() (goOnline +
 *     flush da fila pendente = retransmissão automática de ações offline).
 *  3. Detecção de congelamento: se o intervalo entre ticks estourar (device
 *     dormiu de verdade) ou a aba voltar a ficar visível/rede voltar, roda
 *     resync completo das tabelas críticas — SEM F5, silencioso.
 */
import { useEffect } from 'react';
import { kickSync, resyncTablesFromRemote, restPullTables, getSyncState } from '@/lib/firebaseSync';

const TICK_MS = 25000;
const FROZEN_GAP_MS = 60000;
const POLL_MS = 8000;
const UNHEALTHY_RELOAD_MS = 30000;
const RELOAD_GUARD_KEY = 'kaven_ka_reload_at';
const RELOAD_GUARD_MIN_GAP_MS = 5 * 60 * 1000;

export function startKeepAlive(tables = []) {
  let stopped = false;
  let wakeLock = null;
  let worker = null;
  let fallbackTimer = null;
  let lastTick = Date.now();

  const requestWakeLock = async () => {
    if (stopped || wakeLock || !('wakeLock' in navigator)) return;
    try {
      wakeLock = await navigator.wakeLock.request('screen');
      wakeLock.addEventListener('release', () => { wakeLock = null; });
    } catch { /* sem permissão/suporte: worker + resync cobrem */ }
  };

  const resync = () => {
    if (!tables.length) return;
    resyncTablesFromRemote(tables).catch(() => {});
  };

  const onTick = () => {
    if (stopped) return;
    const now = Date.now();
    const gap = now - lastTick;
    lastTick = now;
    kickSync();
    if (gap > FROZEN_GAP_MS) {
      // eslint-disable-next-line no-console
      console.log(`[KEEP-ALIVE] device dormiu ~${Math.round(gap / 1000)}s — resync automático`);
      resync();
    }
  };

  try {
    const src = `setInterval(function(){ postMessage('t'); }, ${TICK_MS});`;
    worker = new Worker(URL.createObjectURL(new Blob([src], { type: 'application/javascript' })));
    worker.onmessage = onTick;
  } catch {
    fallbackTimer = setInterval(onTick, TICK_MS);
  }

  // v8.06 — POLLER DE RECUPERAÇÃO ATIVA (8s): não depende do socket do SDK.
  // Se a conexão em tempo real cai ("Sincronização Parada"), faz PULL REST
  // direto por HTTP (restPullTables) — os dados reais chegam mesmo com o
  // socket zumbi. Persistindo o problema por 30s+, executa UM reload
  // controlado (mín. 5 min entre reloads — loop impossível) pra reabrir os
  // sockets do zero.
  let unhealthySince = 0;
  const pollTimer = setInterval(async () => {
    if (stopped || document.visibilityState !== 'visible') return;
    if (!navigator.onLine) { unhealthySince = 0; return; }
    let connected = true;
    try { connected = getSyncState().connected !== false; } catch {}
    if (connected) { unhealthySince = 0; return; }
    if (!unhealthySince) unhealthySince = Date.now();
    kickSync();
    const pulled = await restPullTables(tables).catch(() => false);
    if (pulled) {
      // eslint-disable-next-line no-console
      console.log('[KEEP-ALIVE] socket caído — dados recuperados via PULL REST');
    }
    if (Date.now() - unhealthySince > UNHEALTHY_RELOAD_MS) {
      const lastReload = Number(localStorage.getItem(RELOAD_GUARD_KEY) || 0);
      if (Date.now() - lastReload > RELOAD_GUARD_MIN_GAP_MS) {
        try { localStorage.setItem(RELOAD_GUARD_KEY, String(Date.now())); } catch {}
        // eslint-disable-next-line no-console
        console.warn('[KEEP-ALIVE] sync perdida há 30s+ — reload controlado para reabrir sockets');
        window.location.reload();
      }
    }
  }, POLL_MS);

  const onVisible = () => {
    if (document.visibilityState !== 'visible') return;
    requestWakeLock();
    kickSync();
    resync();
  };
  const onOnline = () => { kickSync(); resync(); };

  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('online', onOnline);
  requestWakeLock();
  kickSync();

  return () => {
    stopped = true;
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('online', onOnline);
    try { if (worker) worker.terminate(); } catch {}
    if (fallbackTimer) clearInterval(fallbackTimer);
    clearInterval(pollTimer);
    try { if (wakeLock) wakeLock.release(); } catch {}
    wakeLock = null;
  };
}

/** Hook para as telas operacionais: keep-alive vivo enquanto montadas. */
export function useKeepAlive(tables) {
  useEffect(() => startKeepAlive(tables), []); // eslint-disable-line react-hooks/exhaustive-deps
}
